/**
 * Groups photos into "moments" from their capture time and GPS position, without any server call.
 *
 *  1. adaptive split on time gaps : a gap is a boundary when its log is above the mean + sigma * std
 *     of all gaps (Platt / Cooper style event detection) and above minGap ; a gap above forcedGap
 *     always splits, whatever the distribution
 *  2. split when two consecutive photos are geoSplit meters apart (haversine)
 *  3. groups bigger than maxGroupSize are split on their largest internal gap
 *  4. lone photos join their nearest neighbour when the gap is small
 *  5. adjacent groups are merged, closest first, down to maxGroups
 *
 * Photos without any date keep their input order and are appended after the dated ones.
 */
fx.ns("curator", class MomentClustering {

    constructor(options)
    {
        options = options || {};

        this.minGap = options.minGap || 15 * 60;           // seconds : never split on a smaller gap
        this.forcedGap = options.forcedGap || 6 * 3600;    // seconds : always split on a bigger gap
        this.sigma = (options.sigma === undefined) ? 1.0 : options.sigma;
        this.geoSplit = options.geoSplit || 500;           // meters
        this.maxGroupSize = options.maxGroupSize || 12;
        this.singletonGap = options.singletonGap || 3600;  // seconds : a lone photo joins its neighbour below this
    }

    /**
     * @param photos     models.Photo[]
     * @param maxGroups  optional upper bound on the number of groups
     * @returns [{ photos : models.Photo[], start : unix, end : unix }]
     */
    cluster(photos, maxGroups)
    {
        let items = MomentClustering.sort(photos).map((photo) => ({
            photo : photo,
            time : MomentClustering.time(photo),
            lat : Number(photo.latitude) || 0,
            lng : Number(photo.longitude) || 0
        }));

        if (!items.length)
            return [];

        // undated photos take the time of the last dated one so they never create artificial gaps
        let last = 0;
        for (let i = 0; i < items.length; i++)
        {
            if (items[i].time)
                last = items[i].time;
            else
                items[i].time = last;
        }

        let groups = this._split(items);
        groups = this._splitOversize(groups);
        groups = this._absorbSingletons(groups);

        if (maxGroups > 0)
            groups = this._mergeTo(groups, maxGroups);

        return groups.map((group) => ({
            photos : group.map((item) => item.photo),
            start : group[0].time,
            end : group[group.length - 1].time
        }));
    }

    /** Capture time when known, upload time otherwise, 0 when nothing is known. */
    static time(photo)
    {
        return Number(photo.date_taken_unix) || Number(photo.date_unix) || 0;
    }

    /** Chronological order, undated photos last in their input order. */
    static sort(photos)
    {
        let dated = [], undated = [];
        for (let i = 0; i < photos.length; i++)
            (MomentClustering.time(photos[i]) ? dated : undated).push(photos[i]);

        dated.sort((a, b) => MomentClustering.time(a) - MomentClustering.time(b));

        return dated.concat(undated);
    }

    static distance(lat1, lng1, lat2, lng2)
    {
        let rad = Math.PI / 180;
        let dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
        let a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

        return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    // ------------------------------------------------------------------ //

    _split(items)
    {
        let gaps = [], logs = [];
        for (let i = 0; i < items.length - 1; i++)
        {
            let gap = Math.max(0, items[i + 1].time - items[i].time);
            gaps.push(gap);
            logs.push(Math.log(1 + gap));
        }

        let mean = 0, std = 0;
        if (logs.length)
        {
            mean = logs.reduce((sum, v) => sum + v, 0) / logs.length;
            std = Math.sqrt(logs.reduce((sum, v) => sum + (v - mean) * (v - mean), 0) / logs.length);
        }

        let threshold = mean + this.sigma * std;

        let groups = [[items[0]]];
        for (let i = 0; i < gaps.length; i++)
        {
            let gap = gaps[i];
            let split = false;

            if (gap >= this.forcedGap)
                split = true;
            else if (gap >= this.minGap && logs[i] > threshold)
                split = true;
            else if (this._far(items[i], items[i + 1]))
                split = true;

            if (split)
                groups.push([]);

            groups[groups.length - 1].push(items[i + 1]);
        }

        return groups;
    }

    _far(a, b)
    {
        if (!a.lat || !a.lng || !b.lat || !b.lng)
            return false;

        return MomentClustering.distance(a.lat, a.lng, b.lat, b.lng) > this.geoSplit;
    }

    _splitOversize(groups)
    {
        let result = [];

        for (let g = 0; g < groups.length; g++)
        {
            let stack = [groups[g]];

            while (stack.length)
            {
                let group = stack.shift();

                if (group.length <= this.maxGroupSize)
                {
                    result.push(group);
                    continue;
                }

                // cut on the largest gap that leaves at least two photos on each side, halves otherwise
                let cut = -1, best = -1;
                for (let i = 1; i < group.length - 2; i++)
                {
                    let gap = group[i + 1].time - group[i].time;
                    if (gap > best)
                    {
                        best = gap;
                        cut = i;
                    }
                }

                if (cut < 0 || best <= 0)
                    cut = Math.floor(group.length / 2) - 1;

                stack.unshift(group.slice(0, cut + 1), group.slice(cut + 1));
            }
        }

        return result;
    }

    _absorbSingletons(groups)
    {
        let changed = true;

        while (changed && groups.length > 1)
        {
            changed = false;

            for (let i = 0; i < groups.length; i++)
            {
                if (groups[i].length != 1)
                    continue;

                let item = groups[i][0];
                let prev = (i > 0) ? groups[i - 1] : null;
                let next = (i < groups.length - 1) ? groups[i + 1] : null;

                let gapPrev = prev ? item.time - prev[prev.length - 1].time : Infinity;
                let gapNext = next ? next[0].time - item.time : Infinity;

                if (Math.min(gapPrev, gapNext) > this.singletonGap)
                    continue;

                if (gapPrev <= gapNext)
                    prev.push(item);
                else
                    next.unshift(item);

                groups.splice(i, 1);
                changed = true;
                break;
            }
        }

        return groups;
    }

    _mergeTo(groups, maxGroups)
    {
        while (groups.length > maxGroups)
        {
            let index = -1, best = Infinity;

            for (let i = 0; i < groups.length - 1; i++)
            {
                let a = groups[i], b = groups[i + 1];
                let gap = Math.max(0, b[0].time - a[a.length - 1].time);

                // closest pair first, the smaller merged group wins ties
                let cost = gap + Math.min(a.length, b.length);
                if (cost < best)
                {
                    best = cost;
                    index = i;
                }
            }

            if (index < 0)
                break;

            groups.splice(index, 2, groups[index].concat(groups[index + 1]));
        }

        return groups;
    }
});
